/**
 * Post engagement store (LR3) — a tiny in-memory source of truth for per-post
 * like / repost / bookmark state + counts, shared across the feed (home.tsx) and
 * the post detail screen so the two never drift.
 *
 * WHY: like/repost optimistic state used to live inside each screen's component
 * state independently. Liking a post in the feed then opening its detail (or
 * vice-versa) showed stale counts, and a socket echo could double-count. This
 * store centralizes the truth:
 *   - Optimistic mutate (applyLike/applyRepost/applyBookmark) flips state + count
 *     immediately and returns a rollback token; the caller fires the REST request
 *     and rolls back on failure.
 *   - Live socket echoes (the server `post:like` / `post:repost` events delivered
 *     to the AUTHOR's personal room) reconcile the count to the server's
 *     authoritative value — DEDUPED by event id so the same echo never applies
 *     twice and an echo of the user's OWN action doesn't double-count.
 *   - Screens subscribe (subscribe / getState) and re-render on change.
 *
 * This is additive + defensive: it never throws, and a screen that doesn't use it
 * keeps working. It holds no network logic — callers own the REST call.
 */

export interface PostEngagement {
  liked: boolean;
  likes: number;
  reposted: boolean;
  reposts: number;
  bookmarked: boolean;
}

type Listener = (state: PostEngagement) => void;

// Per-post current engagement state.
const states = new Map<string, PostEngagement>();
// Per-post listener sets (feed card + detail screen can both watch one post).
const listeners = new Map<string, Set<Listener>>();

// Dedupe ring for live socket echoes: each authoritative count update from the
// server carries a stable event id (`${postId}:${kind}:${actorId}:${liked}`-ish);
// we drop a repeat so a re-emitted / replayed echo can't double-apply.
const recentEchoIds: string[] = [];
function echoAlreadySeen(id: string): boolean {
  if (!id) return false;
  if (recentEchoIds.includes(id)) return true;
  recentEchoIds.push(id);
  if (recentEchoIds.length > 200) recentEchoIds.shift();
  return false;
}

function notify(postId: string) {
  const set = listeners.get(postId);
  const state = states.get(postId);
  if (!set || !state) return;
  set.forEach((cb) => {
    try {
      cb(state);
    } catch {
      // a bad listener must never break engagement delivery
    }
  });
}

/**
 * Seed the store with a post's server-truth engagement (called when a post first
 * renders). Does NOT clobber an existing optimistic state for the same post —
 * once the user has interacted, their local truth wins until a live echo or an
 * explicit reseed with a fresher server value. We reseed counts but preserve the
 * user's own liked/reposted/bookmarked toggles if they already diverged.
 */
export function seedEngagement(postId: string, e: Partial<PostEngagement>): PostEngagement {
  if (!postId) {
    return { liked: false, likes: 0, reposted: false, reposts: 0, bookmarked: false };
  }
  const existing = states.get(postId);
  if (existing) {
    // Keep the user's interaction flags; refresh counts only if we have no
    // pending local divergence (best-effort: take the max so a server refetch
    // never visibly drops a count the user just bumped).
    const next: PostEngagement = {
      liked: existing.liked,
      reposted: existing.reposted,
      bookmarked: existing.bookmarked,
      likes: Math.max(existing.likes, e.likes ?? existing.likes),
      reposts: Math.max(existing.reposts, e.reposts ?? existing.reposts),
    };
    states.set(postId, next);
    return next;
  }
  const seeded: PostEngagement = {
    liked: e.liked ?? false,
    likes: e.likes ?? 0,
    reposted: e.reposted ?? false,
    reposts: e.reposts ?? 0,
    bookmarked: e.bookmarked ?? false,
  };
  states.set(postId, seeded);
  return seeded;
}

export function getEngagement(postId: string): PostEngagement | undefined {
  return states.get(postId);
}

export function subscribe(postId: string, cb: Listener): () => void {
  if (!postId) return () => {};
  let set = listeners.get(postId);
  if (!set) {
    set = new Set();
    listeners.set(postId, set);
  }
  set.add(cb);
  return () => {
    const s = listeners.get(postId);
    if (s) s.delete(cb);
  };
}

/**
 * Optimistic LIKE toggle. Returns a rollback() the caller invokes if the REST
 * request fails. `desiredLiked` is the state AFTER the toggle (true = like).
 */
export function applyLike(postId: string, desiredLiked: boolean): () => void {
  const cur = states.get(postId) ?? seedEngagement(postId, {});
  if (cur.liked === desiredLiked) return () => {};
  const prevLikes = cur.likes;
  const next: PostEngagement = {
    ...cur,
    liked: desiredLiked,
    likes: Math.max(0, cur.likes + (desiredLiked ? 1 : -1)),
  };
  states.set(postId, next);
  notify(postId);
  return () => {
    const c = states.get(postId);
    if (!c) return;
    states.set(postId, { ...c, liked: !desiredLiked, likes: prevLikes });
    notify(postId);
  };
}

/** Optimistic REPOST toggle. Returns rollback(). */
export function applyRepost(postId: string, desiredReposted: boolean): () => void {
  const cur = states.get(postId) ?? seedEngagement(postId, {});
  if (cur.reposted === desiredReposted) return () => {};
  const prevReposts = cur.reposts;
  const next: PostEngagement = {
    ...cur,
    reposted: desiredReposted,
    reposts: Math.max(0, cur.reposts + (desiredReposted ? 1 : -1)),
  };
  states.set(postId, next);
  notify(postId);
  return () => {
    const c = states.get(postId);
    if (!c) return;
    states.set(postId, { ...c, reposted: !desiredReposted, reposts: prevReposts });
    notify(postId);
  };
}

/** Optimistic BOOKMARK toggle (no counter). Returns rollback(). */
export function applyBookmark(postId: string, desiredBookmarked: boolean): () => void {
  const cur = states.get(postId) ?? seedEngagement(postId, {});
  if (cur.bookmarked === desiredBookmarked) return () => {};
  const next: PostEngagement = { ...cur, bookmarked: desiredBookmarked };
  states.set(postId, next);
  notify(postId);
  return () => {
    const c = states.get(postId);
    if (!c) return;
    states.set(postId, { ...c, bookmarked: !desiredBookmarked });
    notify(postId);
  };
}

/**
 * Reconcile a post's like count from an authoritative server socket echo
 * (`post:like` delivered to the author's room). `echoId` dedupes replays. We set
 * the count to the server's value (authoritative) but DO NOT flip the local
 * user's own `liked` flag from someone else's like — that flag is the viewer's
 * own relationship to the post. Idempotent + safe to call for posts not yet seen.
 */
export function reconcileLikeCount(
  postId: string,
  serverLikeCount: number,
  echoId: string
): void {
  if (!postId || !Number.isFinite(serverLikeCount)) return;
  if (echoAlreadySeen(echoId)) return;
  const cur = states.get(postId);
  if (!cur) {
    // Not currently rendered/seeded — remember the count so a later seed adopts it.
    states.set(postId, { liked: false, likes: serverLikeCount, reposted: false, reposts: 0, bookmarked: false });
    return;
  }
  if (cur.likes === serverLikeCount) return;
  states.set(postId, { ...cur, likes: serverLikeCount });
  notify(postId);
}

/** Reconcile a post's repost count from the authoritative `post:repost` echo. */
export function reconcileRepostCount(
  postId: string,
  serverRepostCount: number,
  echoId: string
): void {
  if (!postId || !Number.isFinite(serverRepostCount)) return;
  if (echoAlreadySeen(echoId)) return;
  const cur = states.get(postId);
  if (!cur) {
    states.set(postId, { liked: false, likes: 0, reposted: false, reposts: serverRepostCount, bookmarked: false });
    return;
  }
  if (cur.reposts === serverRepostCount) return;
  states.set(postId, { ...cur, reposts: serverRepostCount });
  notify(postId);
}

/** Test/dev helper: clear everything (e.g. on logout). Never throws. */
export function resetEngagement(): void {
  states.clear();
  listeners.clear();
  recentEchoIds.length = 0;
}
